import React, { useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Dialog, Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'
import { AccountsPayable, CostType } from 'models/AccountsPayable'
import { DocumentParams } from 'models/Document'
import moment from 'moment'
import { useAccountsPayableContext } from 'templates/AccountsPayable/context'

import Can from 'components/Can'
import { useDetailCompany } from 'components/DetailCompany'
import MerchantBadge from 'components/MerchantBadge'
import ChipTag from 'components/Tag'

import { formatMoney } from 'utils'
import { deleteDocumentFile } from 'utils/documentFiles'
import { openDocumentViewer } from 'utils/documentViewer'

import { theme } from 'styles/theme'

import Item from '../../Item'
import * as S from './styles'

interface ExpensesDetailsContentProps {
  expenseDetails: AccountsPayable
  uuid: string
  getDetailsRecurrenceExpenses: () => Promise<void>
  isRecurrent: boolean
  documentFile: DocumentParams | undefined
  documentFiles?: DocumentParams[]
  description?: string
}
const ExpensesDetailsContent = ({
  expenseDetails,
  isRecurrent,
  documentFile,
  documentFiles,
  description,
  getDetailsRecurrenceExpenses
}: ExpensesDetailsContentProps) => {
  const { setReloadTable } = useAccountsPayableContext()
  // SC-10999: empresa da despesa, só na visão multi-empresas
  const company = useDetailCompany(expenseDetails?.uuid)
  const [attachmentToDelete, setAttachmentToDelete] =
    useState<DocumentParams | null>(null)
  const costTypeMapperText: Partial<Record<CostType, number>> = {
    fixed: 1,
    variable: 2,
    single: 3
  }
  const getDetailedExpenseType = (costTypeParam: CostType) => {
    const costType = costTypeMapperText[costTypeParam]
    const isSingle = costType === 3
    let text = ''
    if (isSingle) {
      text = 'Pontual/'
      if (expenseDetails?.totalCycles > 1) {
        text += 'Parcelada'
        return text
      } else {
        text += 'À vista'
        return text
      }
    } else {
      text = 'Recorrente/'
      if (costType === 1) {
        text += 'Fixa'
        return text
      } else {
        text += 'Variável'
        return text
      }
    }
  }
  // SC-10805: a despesa pode ter ate 3 comprovantes. Quando o pai so passa o singular
  // (telas ainda nao migradas), cai de volta para uma lista de um item.
  const attachments = documentFiles?.length
    ? documentFiles
    : documentFile
    ? [documentFile]
    : []

  const handleDownloadFile = async (attachment: DocumentParams) => {
    if (!attachment?.url) return

    try {
      const response = await fetch(attachment.url)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = attachment?.name || 'documento'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error('Ocorreu um erro ao baixar o arquivo')
    }
  }

  const handleViewFile = (attachment: DocumentParams) => {
    if (!attachment?.url) return

    openDocumentViewer({
      url: attachment.url,
      filename: attachment.name || '',
      fallbackToDirectOpen: true
    })
  }

  // Excluir o anexo direto do detalhe evita o desvio de abrir "Editar" só para remover
  // um comprovante. Confirma antes: a exclusão vale na hora e não tem desfazer.
  const handleDeleteFile = async () => {
    const alvo = attachmentToDelete
    setAttachmentToDelete(null)
    if (!alvo?.id) return

    const excluiu = await deleteDocumentFile(
      `/v1/accounts_payables/documents/${alvo.id}`
    )

    // O ModalDetailsExpenses refaz o GET quando reloadTable muda, então a lista volta
    // do servidor em vez de ser remendada na tela.
    if (excluiu) setReloadTable((prev) => !prev)
  }
  const frequencyTypeMapper = (
    frequencyType: string,
    frequencyNumber: number
  ) => {
    switch (frequencyType) {
      case 'months':
        switch (frequencyNumber) {
          case 3:
            return 'Trimestral'
          case 6:
            return 'Semestral'
          default:
            return 'Mensal'
        }

      case 'days':
        return frequencyNumber === 1
          ? 'Diário'
          : 'Não foi possível mapear a frequência.'

      case 'weeks':
        return frequencyNumber === 1
          ? 'Semanal'
          : 'Não foi possível mapear a frequência.'

      case 'years':
        return frequencyNumber === 1
          ? 'Anual'
          : 'Não foi possível mapear a frequência.'

      default:
        return 'Não foi possível mapear a frequência.'
    }
  }

  return (
    <>
      {company && (
        <Item
          textKey="Empresa"
          value={
            <MerchantBadge
              name={company.name}
              logoUrl={company.logo_url}
              size={20}
              variant="smBold"
            />
          }
        />
      )}
      <Item textKey={'Beneficiário'} value={expenseDetails?.payee.name || ''} />
      {/* Quando há rateio (>1 allocation), o detalhamento vai no accordion
          "Rateio" do modal — aqui só exibimos Centro de custo/Categoria
          quando a despesa não é rateada. */}
      {(expenseDetails?.allocations?.length ?? 0) <= 1 && (
        <>
          {expenseDetails?.allocations?.[0]?.costCenter?.name && (
            <Item
              textKey="Centro de custo"
              value={expenseDetails?.allocations?.[0]?.costCenter?.name || ''}
            />
          )}
          {expenseDetails?.allocations?.[0]?.category?.name && (
            <Item
              textKey="Categoria"
              value={expenseDetails?.allocations?.[0]?.category?.name || ''}
            />
          )}
        </>
      )}

      {expenseDetails?.competenceDate && (
        <Item
          textKey="Competência"
          value={moment(expenseDetails?.competenceDate).format('MM/YYYY') || ''}
        />
      )}
      <Item
        textKey={
          expenseDetails?.firstDueDate
            ? 'Vencimento da primeira despesa'
            : 'Vencimento'
        }
        value={
          moment(
            expenseDetails?.firstDueDate ?? expenseDetails?.dueDate
          ).format('DD/MM/YYYY') || ''
        }
      />
      <Item
        textKey="Tipo despesa"
        value={getDetailedExpenseType(expenseDetails?.costType) || ''}
      />

      <Item
        textKey={
          getDetailedExpenseType(expenseDetails?.costType).includes('Parcelada')
            ? 'Valor da parcela'
            : 'Valor'
        }
        value={formatMoney(expenseDetails?.amountBilled) || ''}
      />
      {expenseDetails?.frequencyNumber && (
        <Item
          textKey="Frequência"
          value={
            isRecurrent
              ? frequencyTypeMapper(
                  expenseDetails.frequencyType as string,
                  expenseDetails.frequencyNumber
                )
              : expenseDetails?.frequencyNumber?.toString() || ''
          }
        />
      )}
      {!!expenseDetails?.totalCycles && !isRecurrent && (
        <Item
          textKey="Ciclo"
          value={
            <Box display="flex" flexWrap="wrap" gap="8px" alignItems="center">
              {expenseDetails?.recurrenceCycle?.toString()}/
              {expenseDetails?.totalCycles?.toString()}
              {!isRecurrent && (
                <S.ButtonRecurrenceView
                  type="button"
                  onClick={() => getDetailsRecurrenceExpenses()}
                >
                  <Icon
                    icon="open"
                    width="21px"
                    height="21px"
                    ml="6px"
                    sx={{ cursor: 'pointer', color: '#CBCBCB' }}
                  />
                </S.ButtonRecurrenceView>
              )}
            </Box>
          }
        />
      )}
      {attachments.length > 0 && (
        <Item
          textKey={
            attachments.length > 1 ? 'Arquivos anexados' : 'Arquivo anexado'
          }
          value={
            <Box display="flex" flexDirection="column" gap="4px" width="100%">
              {attachments.map((attachment) => (
                <Box
                  key={attachment.id || attachment.url}
                  display="flex"
                  sx={{
                    cursor: 'pointer',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}
                  onClick={() => handleViewFile(attachment)}
                >
                  <Typography
                    variant="smBold"
                    color="#000"
                    title={attachment?.name || ''}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      // O nome ocupa a linha toda que sobra: com 3 anexos o texto e a
                      // parte mais util da linha, e o `30ch` antigo cortava cedo demais
                      // deixando metade do modal vazia (SC-10805).
                      minWidth: 0
                    }}
                  >
                    {attachment?.name}
                  </Typography>
                  <Icon sx={{ ml: '8px' }} size={15} icon="visibility" />
                  <Box
                    display="flex"
                    sx={{ cursor: 'pointer' }}
                    onClick={(event) => {
                      event.stopPropagation()
                      handleDownloadFile(attachment)
                    }}
                  >
                    <Icon sx={{ ml: '8px' }} icon="download" />
                  </Box>
                  {/* Mesma permissão que guarda Editar, Duplicar e Excluir despesa neste
                      modal (ModalDetailsExpenses) e que a API exige para alterar a
                      despesa: excluir comprovante é alteração, não leitura. */}
                  <Can permission="accounts_payable:manage">
                    <Tooltip arrow placement="top" title="Excluir arquivo">
                      <Box
                        display="flex"
                        aria-label="Excluir arquivo"
                        sx={{ cursor: 'pointer' }}
                        onClick={(event) => {
                          event.stopPropagation()
                          setAttachmentToDelete(attachment)
                        }}
                      >
                        <Icon
                          sx={{ ml: '8px', color: theme.palette.neutral[400] }}
                          icon="cancel"
                        />
                      </Box>
                    </Tooltip>
                  </Can>
                </Box>
              ))}
            </Box>
          }
        />
      )}
      {description && (
        <Item
          textKey="Descrição"
          value={
            <Box
              display="flex"
              flexWrap="wrap"
              gap="8px"
              mt={1}
              alignItems="center"
              fontWeight={400}
            >
              {description}
            </Box>
          }
        />
      )}

      {/* Tags Display - Only show when tags exist */}
      {expenseDetails?.tags && expenseDetails.tags.length > 0 && (
        <Item
          textKey="Tags"
          value={
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {expenseDetails.tags.map((tag) => (
                <ChipTag
                  key={tag.id}
                  tag={tag}
                  // No onDelete prop - this is display only
                />
              ))}
            </Box>
          }
        />
      )}

      <Dialog
        title="Confirma excluir este arquivo?"
        description={attachmentToDelete?.name || ''}
        maxWidth="xs"
        icon="delete"
        isOpenDialog={!!attachmentToDelete}
        setIsOpenDialog={(open) => {
          if (!open) setAttachmentToDelete(null)
        }}
        cancelButton
      >
        <Button fullWidth onClick={handleDeleteFile}>
          Excluir
        </Button>
      </Dialog>
    </>
  )
}

export default ExpensesDetailsContent
